class FakeSwatches extends HTMLElement {
  constructor() {
    super();
    this.container = this.closest('.card-wrapper');
    this.swatchInputs = this.querySelectorAll('.swatch-input__input');
    this.swatchLabels = this.querySelectorAll('.swatch-input__label');
    this.originalImageState = null;
    this.subFunctionsCallBack();
  }

  subFunctionsCallBack() {
    this.addEventListener('change', (e) => this.handleInputChange(e));
    this.addEventListener('click', (e) => this.handleLabelClick(e));
    this.addEventListener('mouseenter', (e) => this.handleHoverEnter(e), true);
    this.addEventListener('mouseleave', (e) => this.handleHoverLeave(e), true);

    if (this.swatchInputs.length > 0) {
      this.swatchInputs[0].checked = true;
      this.selectSwatch(this.swatchLabels[0]);
    }
  }

  handleInputChange(e) {
    if (e.target.matches('.swatch-input__input')) {
      const label = e.target.closest('.swatch-input__label');
      if (label) this.selectSwatch(label);
    }
  }

  handleLabelClick(e) {
    const label = e.target.closest('.swatch-input__label');
    if (label) {
      const input = label.querySelector('.swatch-input__input');
      if (input) {
        input.checked = true;
        this.selectSwatch(label);
      }
    }
  }

  handleHoverEnter(e) {
    const label = e.target.closest('.swatch-input__label');
    if (label?.dataset.image) {
      this.previewImage(label.dataset.image);
    }
  }

  handleHoverLeave(e) {
    if (e.target.closest('.swatch-input__label')) {
      this.restoreImage();
    }
  }

  selectSwatch(label) {
    if (!label) return;
    this.updateCardData(label);
    this.updateSwatchVisualState(label);
    this.originalImageState = null;
  }

  updateCardData(label) {
    const { id, variantId, url, image, material } = label.dataset;

    this.updateLink('.card__media a', url);
    this.updateLink('.card-information__text', url);

    if (image) this.updateCardImage(image);
    if (variantId) this.updateAddToCartButton(variantId);
    if (material) this.updateMaterial(material);

    this.setContainerData({
      'data-selected-product-id': id,
      'data-selected-variant-id': variantId,
      'data-selected-product-url': url,
    });

    this.dispatchEvent(
      new CustomEvent('swatch-changed', {
        detail: { productId: id, variantId, productUrl: url, productImage: image, container: this.container },
        bubbles: true,
      })
    );
  }

  updateCardImage(imageUrl) {
    const formattedUrl = this.formatImageUrl(imageUrl);
    const img = this.container.querySelector('.card--image-animate.media img');
    const source = this.container.querySelector('.card--image-animate.media source');

    if (img) {
      img.src = img.srcset = formattedUrl;
      img.setAttribute('data-src', formattedUrl);
    }
    if (source) source.srcset = formattedUrl;
  }

  previewImage(imageUrl) {
    if (!this.originalImageState) this.saveOriginalImageState();

    const img = this.container.querySelector('.card--image-animate.media img');
    if (img) img.classList.add('previewing');

    this.updateCardImage(imageUrl);
  }

  restoreImage() {
    const img = this.container.querySelector('.card--image-animate.media img');
    if (img) img.classList.remove('previewing');

    if (this.originalImageState) this.restoreOriginalImageState();
  }

  saveOriginalImageState() {
    const img = this.container.querySelector('.card--image-animate.media img');
    const source = this.container.querySelector('.card--image-animate.media source');

    this.originalImageState = {
      imgSrc: img?.src,
      imgSrcset: img?.srcset,
      imgDataSrc: img?.getAttribute('data-src'),
      sourceSrcset: source?.srcset,
    };
  }

  restoreOriginalImageState() {
    const img = this.container.querySelector('.card--image-animate.media img');
    const source = this.container.querySelector('.card--image-animate.media source');

    if (img) {
      img.src = this.originalImageState.imgSrc;
      img.srcset = this.originalImageState.imgSrcset;
      img.setAttribute('data-src', this.originalImageState.imgDataSrc);
    }
    if (source) {
      source.srcset = this.originalImageState.sourceSrcset;
    }
  }

  formatImageUrl(url) {
    if (url.startsWith('http') || url.includes('cdn.shopify.com')) return url;
    if (url.startsWith('/')) return `https:${url}`;
    return `https://cdn.shopify.com${url}`;
  }

  updateAddToCartButton(variantId) {
    this.container.querySelector('add-to-cart')?.setAttribute('data-variant-id', variantId);
  }

  updateMaterial(material) {
    const materialElement = this.querySelector('.swatch-input__material');
    if (materialElement) {
      materialElement.textContent = material;
      materialElement.setAttribute('data-product-material', material);
    }
  }

  updateSwatchVisualState(selectedLabel) {
    this.swatchLabels.forEach((label) => label.classList.remove('is-active'));
    selectedLabel.classList.add('is-active');
  }

  updateLink(selector, url) {
    this.container.querySelector(selector)?.setAttribute('href', url);
  }

  setContainerData(data) {
    Object.entries(data).forEach(([attr, value]) => this.container.setAttribute(attr, value));
  }

  selectSwatchByProductId(productId) {
    const label = [...this.swatchLabels].find((l) => l.dataset.id === productId);
    if (label) {
      label.querySelector('.swatch-input__input').checked = true;
      this.selectSwatch(label);
    }
  }

  selectSwatchByVariantId(variantId) {
    const label = [...this.swatchLabels].find((l) => l.dataset.variantId === variantId);
    if (label) {
      label.querySelector('.swatch-input__input').checked = true;
      this.selectSwatch(label);
    }
  }

  getSelectedProductData() {
    const input = [...this.swatchInputs].find((i) => i.checked);
    const label = input?.closest('.swatch-input__label');
    return label
      ? {
          productId: label.dataset.id,
          variantId: label.dataset.variantId,
          productUrl: label.dataset.url,
          productImage: label.dataset.image,
          material: label.dataset.material,
        }
      : null;
  }
}

customElements.define('fake-swatches', FakeSwatches);
